import { cloneDeep, set } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import {
  HotelIngredient,
  isOwnStayIngredient,
} from '@apps/main/modules/recipe/ingredients';

import type {
  HotelFilterPillData,
  HotelFilterUpdate,
  HotelFiltersForm,
} from '../model/hotelFilters';
import { DEFAULT_HOTEL_FILTERS, HotelFilter } from '../model/hotelFilters';

import {
  getActiveFilters,
  getFullStarsArray,
  getHotelFilterFromIngredient,
} from './hotellFiltersStoreLogic';

export const useHotelFiltersStore = defineStore('HotelFilters', () => {
  // State
  const form = ref<HotelFiltersForm>(cloneDeep(DEFAULT_HOTEL_FILTERS));

  // Actions
  function setFiltersValuesFromIngredient(
    stayIngredient: HotelIngredient | OwnStayIngredient
  ): void {
    const filters = getHotelFilterFromIngredient(
      isOwnStayIngredient(stayIngredient)
        ? HotelIngredient.defaultFilters
        : stayIngredient.filters
    );
    form.value = filters;
  }

  function updateFormValue(filterUpdate: HotelFilterUpdate): void {
    switch (filterUpdate.type) {
      case HotelFilter.RatingStars: {
        const newValues = getFullStarsArray(filterUpdate.value);
        set(form.value, filterUpdate.type, newValues);
        break;
      }
      case HotelFilter.HotelName: {
        set(form.value, filterUpdate.type, filterUpdate.value.trim());
        break;
      }
      default:
        set(form.value, filterUpdate.type, filterUpdate.value);
        break;
    }
  }

  function resetAllFiltersToDefault(): void {
    form.value = cloneDeep(DEFAULT_HOTEL_FILTERS);
  }

  function clearMealFilters(): void {
    form.value.meals = DEFAULT_HOTEL_FILTERS.meals;
  }

  function clearAmenitiesFilters(): void {
    form.value.amenities = DEFAULT_HOTEL_FILTERS.amenities;
  }

  function clearAccommodationTypeFilters(): void {
    form.value.typeOfAccommodation = DEFAULT_HOTEL_FILTERS.typeOfAccommodation;
  }

  // Getters
  const activeFilters = computed<HotelFilterPillData[]>(() =>
    getActiveFilters(form.value)
  );

  return {
    //State
    form,

    // Actions
    setFiltersValuesFromIngredient,
    updateFormValue,
    resetAllFiltersToDefault,
    clearMealFilters,
    clearAmenitiesFilters,
    clearAccommodationTypeFilters,

    // Getters
    activeFilters,
  };
});
